import React, { useState } from "react";
import { ShieldCheck, KeyRound, Mail, Loader2 } from "lucide-react";
import { loadSession, changePassword, forceLogout } from "../services/api";
import { usePageTitle } from "../hooks/usePageTitle";

export function SettingsPage() {
  usePageTitle("Settings");
  const session = loadSession();
  if (!session) return null;
  const op = session.operator;

  const [changingPw, setChangingPw] = useState(false);
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [pwLoading, setPwLoading] = useState(false);
  const [pwError, setPwError] = useState<string | null>(null);
  const [pwSuccess, setPwSuccess] = useState(false);

  function resetForm() {
    setCurrent(""); setNext(""); setConfirm("");
    setPwError(null); setPwSuccess(false);
  }

  async function onChangePw(e: React.FormEvent) {
    e.preventDefault();
    if (next !== confirm) { setPwError("New passwords don't match."); return; }
    if (next.length < 8) { setPwError("New password must be at least 8 characters."); return; }
    setPwError(null);
    setPwLoading(true);
    try {
      await changePassword(current, next);
      setPwSuccess(true);
    } catch (err: any) {
      setPwError(err.message ?? "Password change failed.");
    } finally {
      setPwLoading(false);
    }
  }

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <span className="eyebrow">Account</span>
          <h1>Settings</h1>
          <p className="muted">Your operator profile and security.</p>
        </div>
      </header>

      <div className="grid-2">
        <div className="card">
          <div className="section-head"><h2>Profile</h2></div>
          <dl className="detail-list">
            <dt>Name</dt><dd>{op.fullName}</dd>
            <dt>Email</dt><dd>{op.email}</dd>
            <dt>Role</dt><dd><span className="pill accent">{op.role}</span></dd>
            <dt>Last login</dt><dd>{op.lastLoginAt ? new Date(op.lastLoginAt).toLocaleString("en-ZA") : "—"}</dd>
          </dl>
        </div>

        <div className="card">
          <div className="section-head"><h2>Security</h2></div>
          <div className="col" style={{ gap: 14 }}>
            <div className="row between">
              <div className="row" style={{ gap: 10 }}>
                <ShieldCheck size={16} style={{ color: "var(--success)" }} />
                <div>
                  <div style={{ fontWeight: 600 }}>Multi-factor authentication</div>
                  <div className="muted">{op.mfaEnrolled ? "Enrolled — required for every sign-in" : "Not enrolled"}</div>
                </div>
              </div>
              <span className={`pill ${op.mfaEnrolled ? "success" : "warn"}`}>
                {op.mfaEnrolled ? "On" : "Off"}
              </span>
            </div>

            <div className="col" style={{ gap: 10 }}>
              <div className="row between">
                <div className="row" style={{ gap: 10 }}>
                  <KeyRound size={16} />
                  <div>
                    <div style={{ fontWeight: 600 }}>Password</div>
                    <div className="muted">Rotated every 90 days in production.</div>
                  </div>
                </div>
                {!changingPw && (
                  <button className="btn btn-ghost btn-sm" onClick={() => { resetForm(); setChangingPw(true); }}>
                    Change
                  </button>
                )}
              </div>

              {changingPw && (
                <form className="form" onSubmit={onChangePw} noValidate style={{ marginTop: 4 }}>
                  {pwSuccess ? (
                    <div className="col" style={{ gap: 8 }}>
                      <p style={{ color: "var(--success)", fontSize: 13, margin: 0 }}>
                        Password changed successfully.
                      </p>
                      <div className="row" style={{ gap: 8, alignItems: "center" }}>
                        <button className="btn btn-primary btn-sm" onClick={() => forceLogout("manual")}>
                          Sign out
                        </button>
                        <button className="btn btn-ghost btn-sm" onClick={() => { setChangingPw(false); resetForm(); }}>
                          Stay signed in
                        </button>
                        <span className="muted" style={{ fontSize: 12 }}>Signing out is recommended</span>
                      </div>
                    </div>
                  ) : (
                    <>
                      {pwError && <div className="login-error" style={{ fontSize: 13 }}>{pwError}</div>}
                      <label style={{ fontSize: 13 }}>
                        Current password
                        <input
                          type="password"
                          autoComplete="current-password"
                          required
                          value={current}
                          onChange={(e) => setCurrent(e.target.value)}
                        />
                      </label>
                      <label style={{ fontSize: 13 }}>
                        New password
                        <input
                          type="password"
                          autoComplete="new-password"
                          required
                          value={next}
                          onChange={(e) => setNext(e.target.value)}
                        />
                      </label>
                      <label style={{ fontSize: 13 }}>
                        Confirm new password
                        <input
                          type="password"
                          autoComplete="new-password"
                          required
                          value={confirm}
                          onChange={(e) => setConfirm(e.target.value)}
                        />
                      </label>
                      <div className="row" style={{ gap: 8 }}>
                        <button className="btn btn-primary btn-sm" type="submit" disabled={pwLoading}>
                          {pwLoading ? <><Loader2 size={14} className="spin" /> Saving…</> : "Save"}
                        </button>
                        <button
                          className="btn btn-ghost btn-sm"
                          type="button"
                          onClick={() => { setChangingPw(false); resetForm(); }}
                        >
                          Cancel
                        </button>
                      </div>
                    </>
                  )}
                </form>
              )}
            </div>

            <div className="row between">
              <div className="row" style={{ gap: 10 }}>
                <Mail size={16} />
                <div>
                  <div style={{ fontWeight: 600 }}>Security alerts</div>
                  <div className="muted">Email me on suspicious activity.</div>
                </div>
              </div>
              <span className="pill success">On</span>
            </div>
          </div>
        </div>

        <div className="card" style={{ gridColumn: "1 / -1" }}>
          <div className="section-head"><h2>About this app</h2></div>
          <p className="muted">
            This is the Orbit SA internal back-office. It is isolated from the customer-facing portal:
            separate origin, separate JWT audience, IP-allowlist enforced at the edge, and every action
            here writes to an immutable audit log. Production deployment also requires SSO + MFA.
          </p>
        </div>
      </div>
    </div>
  );
}
